import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Pencil } from 'lucide-react';
import { Account } from '@/types/account';
import { getAccount } from '@/services/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { IntegrationSnippets } from '@/components/integration/IntegrationSnippets';

interface AccountSheetProps {
  open: boolean;
  accountToken: string | null;
  onOpenChange: (open: boolean) => void;
  onEdit: (account: Account) => void;
}

export function AccountSheet({ open, accountToken, onOpenChange, onEdit }: AccountSheetProps) {
  const { instance } = useMsal();
  const [account, setAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (open && accountToken) {
      fetchAccount();
    }
    if (!open) {
      setAccount(null);
      setError('');
    }
  }, [open, accountToken]);

  const fetchAccount = async () => {
    if (!accountToken) return;

    setIsLoading(true);
    setError('');

    try {
      const data = await getAccount(instance, accountToken);
      setAccount(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load account';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    if (account) {
      onEdit(account);
      onOpenChange(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px] sm:max-w-[540px] overflow-y-auto p-6">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Account Details</SheetTitle>
            {account && (
              <Badge variant={account.isActive ? 'default' : 'secondary'}>
                {account.isActive ? 'Active' : 'Inactive'}
              </Badge>
            )}
          </div>
          {account && (
            <SheetDescription className="font-mono text-xs cx-mask">
              {account.accountToken}
            </SheetDescription>
          )}
        </SheetHeader>

        {isLoading && (
          <div className="mt-6 space-y-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={fetchAccount}>
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !error && account && (
          <div className="mt-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Basic Information</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium">{account.name}</p>
                </div>
                {account.productName && (
                  <div>
                    <p className="text-muted-foreground">Product</p>
                    <p className="font-medium">{account.productName}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Remark</p>
                  <p className="font-medium">{account.remark || '—'}</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Configuration */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Configuration</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Feedback Table</p>
                  <p className="font-mono text-xs">{account.userFeedbackTableName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Feedback Table</p>
                  <p className="font-mono text-xs">{account.userLastFeedbackTableName}</p>
                </div>
                {account.allowedDomains.length > 0 && (
                  <div>
                    <p className="text-muted-foreground">Allowed Domains</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {account.allowedDomains.map((domain, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {domain}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Integration */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Integration</h4>
              <IntegrationSnippets accountToken={account.accountToken} compact />
            </div>

            <Separator />

            {/* Metadata */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Metadata</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Created By</p>
                  <p className="font-medium truncate" title={account.createdBy}>
                    {account.createdBy}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">{formatDate(account.createdTimestamp)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Updated By</p>
                  <p className="font-medium truncate" title={account.updatedBy}>
                    {account.updatedBy}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Updated</p>
                  <p className="font-medium">{formatDate(account.updatedTimestamp)}</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex gap-2">
              <Button onClick={handleEdit} className="flex-1">
                <Pencil className="mr-2 size-4" />
                Edit Account
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
